import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import OjentLogo from "@/components/OjentLogo";

export default function Signup() {
  const { signup, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    restaurant_name: "", owner_name: "", email: "", password: "", phone: "", city: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/onboarding" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup(form);
      navigate("/onboarding");
    } catch (err) {
      const network = !err.response ? "Could not reach the server. Please try again." : "";
      setError(formatApiError(err.response?.data?.detail, network || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[var(--kul-charcoal)]">
        <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=80"
          alt="restaurant" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--kul-charcoal)] via-transparent to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <OjentLogo markSize={44} variant="dark" />
          <div>
            <h1 className="font-display text-white text-4xl font-800 leading-tight">
              Onboard your restaurant in minutes.
            </h1>
            <p className="text-neutral-300 mt-4 max-w-md">
              You get a private workspace: menu, tables, staff, POS, kitchen, and guest QR pages on your own URL.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
          <h2 className="font-display text-3xl font-800 tracking-tight">Create restaurant</h2>
          <p className="text-neutral-500 mt-1 text-sm">Owner account + a new isolated outlet.</p>
          <div className="mt-6 space-y-3">
            <div className="space-y-1.5">
              <Label>Restaurant name</Label>
              <Input required value={form.restaurant_name} onChange={set("restaurant_name")} placeholder="Cafe Sunrise" data-testid="signup-restaurant" />
            </div>
            <div className="space-y-1.5">
              <Label>Your name</Label>
              <Input required value={form.owner_name} onChange={set("owner_name")} placeholder="Priya Shah" data-testid="signup-owner" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input value={form.city} onChange={set("city")} placeholder="Pune" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={set("phone")} placeholder="98xxxxxxxx" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} placeholder="owner@cafe.com" data-testid="signup-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password (8+ characters)</Label>
              <Input required type="password" minLength={8} value={form.password} onChange={set("password")} data-testid="signup-password" />
            </div>
            {error && <p className="text-sm text-[var(--kul-danger)]" data-testid="signup-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="signup-submit"
              className="w-full h-11 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create and continue"}
            </Button>
            <p className="text-sm text-center text-neutral-500">
              Already have an account? <Link to="/login" className="text-[var(--kul-primary)] font-medium">Log in</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
